/**
 * Service-worker registration and the update prompt.
 *
 * THE RISK THIS MANAGES: once a service worker is installed, a returning user
 * keeps the OLD bundle until every tab closes. BeeNexa deploys by copying a new
 * build over the old one, so without a prompt a user could sit on a stale
 * version for days and never know. `onUpdate` is how the app tells them.
 *
 * Registration is deliberately NOT automatic on import — `register()` is called
 * from Fair Tracker, so the worker only installs for someone who actually opens
 * the app that needs it. Other BeeNexa users never get one.
 */

const SW_URL = '/sw-fairtracker.js';

let registration = null;

export function supported() {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/**
 * @param onUpdate called with a `reload()` function when a new build is waiting
 */
export async function register({ onUpdate } = {}) {
  if (!supported()) return null;

  /* NEVER IN DEVELOPMENT.
     The worker caches build assets cache-first, and in dev that includes
     webpack's `*.hot-update.js` chunks — so every edit appeared to do nothing
     until a hard reload bypassed the worker. Hours were lost to "the CSS
     change isn't applying" before this was spotted. A dev build has no use for
     offline anyway; `npm run build && npx serve -s build` is how you exercise
     the real thing. Any worker left over from a dev session is torn down by
     `cleanupDevWorker()` below. */
  if (process.env.NODE_ENV !== 'production') {
    cleanupDevWorker();
    return null;
  }

  // localhost over http is a secure context; everything else must be https, or
  // registration throws rather than returning null.
  if (window.location.protocol !== 'https:' && !isLocalhost()) return null;

  try {
    registration = await navigator.serviceWorker.register(SW_URL);

    // Already waiting when we arrived (a previous tab installed it).
    if (registration.waiting && navigator.serviceWorker.controller) {
      notify(registration, onUpdate);
    }

    registration.addEventListener('updatefound', () => {
      const incoming = registration.installing;
      if (!incoming) return;
      incoming.addEventListener('statechange', () => {
        if (incoming.state === 'installed' && navigator.serviceWorker.controller) {
          notify(registration, onUpdate);
        }
      });
    });

    return registration;
  } catch (e) {
    // A failed registration must never break the app — it only costs offline.
    return null;
  }
}

function notify(reg, onUpdate) {
  if (typeof onUpdate !== 'function') return;
  onUpdate(() => {
    if (reg.waiting) reg.waiting.postMessage('SKIP_WAITING');
    // controllerchange fires once the new worker takes over; reload then so the
    // page is never left half on the old bundle.
    const reload = () => window.location.reload();
    navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true });
    // Fallback in case controllerchange does not fire.
    setTimeout(reload, 1200);
  });
}

export async function unregister() {
  if (!supported()) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  } catch (e) {
    /* nothing to do */
  }
}

/**
 * Remove a worker (and its caches) left behind by an earlier dev session.
 *
 * Without this, a developer who ran the app before this guard existed keeps a
 * registered worker serving stale `hot-update.js` chunks forever — the symptom
 * is edits that silently do nothing. Self-healing beats a README note.
 */
async function cleanupDevWorker() {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    const ours = regs.filter((r) => r.active && /sw-fairtracker\.js$/.test(r.active.scriptURL));
    if (!ours.length) return;
    await Promise.all(ours.map((r) => r.unregister()));
    const names = await caches.keys();
    await Promise.all(names.filter((n) => n.startsWith('ft-')).map((n) => caches.delete(n)));
    // eslint-disable-next-line no-console
    console.info(
      '[Fair Tracker] Removed the dev service worker and its caches. ' +
        'Reload once more to be sure you are on fresh assets.',
    );
  } catch (e) {
    /* best effort */
  }
}

/** True when the page is running installed-to-home-screen. */
export function isInstalled() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari's own flag, which predates display-mode.
    window.navigator.standalone === true
  );
}

function isLocalhost() {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}
